/**
 * Экран выбора сервера — ПЕРВЫЙ экран приложения (Антон, 2026-09-13).
 *
 * Зачем отдельный шаг до входа: адрес homeserver раньше вшивался при сборке,
 * и «зайти на прод» означало пересобрать приложение. Теперь сервер выбирается
 * здесь и запоминается; сессии прода и стенда живут раздельно, поэтому
 * переключение туда-обратно не требует логина заново.
 */
import { useState } from "react";
import { useT } from "../i18n";

import { SERVER_CHOICES, setHomeserverUrl } from "../config";
import { color, font, radius, space } from "./theme";

export function ServerPicker({
  onSelect,
  current,
}: {
  onSelect: () => void;
  /** Текущий сервер, если экран открыт как смена сервера, а не первый запуск. */
  current?: string | null;
}) {
  const t = useT();
  const [custom, setCustom] = useState("");
  const [error, setError] = useState<string | null>(null);

  const choose = (url: string) => {
    try {
      setHomeserverUrl(url);
      setError(null);
      onSelect();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <main style={wrap}>
      <section style={card}>
        <h1 style={title}>{t("app.serverChoose")}</h1>
        <p style={lead}>{t("app.serverConnectDescription")}</p>

        <div style={{ display: "grid", gap: space.md }}>
          {SERVER_CHOICES.map((server) => {
            const active = current === server.url;
            return (
              <button
                key={server.url}
                type="button"
                style={{
                  ...choiceButton,
                  borderColor: active ? color.accent : color.border,
                }}
                onClick={() => choose(server.url)}
              >
                <span style={{ fontWeight: 600 }}>
                  {server.label}
                  {active ? t("server.current") : ""}
                </span>
                <small style={{ display: "block", color: color.textDim, marginTop: 2 }}>
                  {server.hint} · {server.url.replace(/^https:\/\//, "")}
                </small>
              </button>
            );
          })}
        </div>

        <label style={customLabel}>{t("app.serverCustom")}<input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={t("server.matrixPlaceholder")}
            style={input}
            onKeyDown={(e) => {
              if (e.key === "Enter" && custom.trim()) choose(custom);
            }}
          />
        </label>
        <button
          type="button"
          style={{ ...choiceButton, width: "100%", textAlign: "center", opacity: custom.trim() ? 1 : 0.5 }}
          disabled={!custom.trim()}
          onClick={() => choose(custom)}
        >{t("app.serverConnectButton")}</button>

        {error && (
          <p role="alert" style={{ color: color.danger, marginBottom: 0 }}>
            {error}
          </p>
        )}
      </section>
    </main>
  );
}

const wrap = {
  minHeight: "100%",
  display: "grid",
  placeItems: "center",
  background: color.bg,
  color: color.text,
  padding: space.xl,
} as const;

const card = {
  width: "min(440px, 100%)",
  padding: 28,
  borderRadius: radius.md,
  background: color.sidebarBg,
  boxShadow: "0 12px 32px rgba(43,42,38,.12)",
  boxSizing: "border-box" as const,
};

const title = { margin: 0, fontFamily: font.family, fontSize: 25 } as const;
const lead = { color: color.textDim, lineHeight: 1.5, fontSize: 14 } as const;

const choiceButton = {
  border: `1px solid ${color.border}`,
  borderRadius: radius.md,
  background: color.bg,
  color: color.text,
  padding: "12px 14px",
  textAlign: "left" as const,
  cursor: "pointer",
  font: "inherit",
};

const customLabel = {
  display: "block",
  marginTop: 22,
  marginBottom: 10,
  fontSize: 13,
  color: color.textDim,
} as const;

const input = {
  display: "block",
  boxSizing: "border-box" as const,
  width: "100%",
  marginTop: 6,
  padding: 10,
  border: `1px solid ${color.borderStrong}`,
  borderRadius: radius.sm,
  background: color.bg,
  color: color.text,
  font: "inherit",
};
